import { ApiService } from '../ApiService';
import type { ApiResponse, PaginatedResponse } from '../types';
import type { BusinessUnitFilterBase } from '../../../types/filters';
import type { TreasuryAccount as LocalTreasuryAccount } from '../../../types/treasury';
import { CurrencyCode } from '../../../config/currency';

/**
 * Types de comptes de trésorerie conformes à la documentation
 */
export type TreasuryAccountType =
  | 'bank'
  | 'microfinance'
  | 'cooperative'
  | 'vsla'
  | 'mobile_money';

/**
 * Types de flux de trésorerie conformes à la documentation
 */
export type CashFlowType = 'inflow' | 'outflow';

/**
 * Catégories de flux de trésorerie conformes à la documentation
 */
export type CashFlowCategory = 
  | 'sales'           // Encaissements clients
  | 'supplier_payments' // Paiements fournisseurs
  | 'salaries'        // Salaires
  | 'taxes'           // Impôts et taxes
  | 'loans'           // Prêts
  | 'investments'     // Investissements
  | 'other';          // Autres

/**
 * Interface compte de trésorerie API - conforme à la documentation
 */
export interface TreasuryAccountApi {
  id: string;
  name: string;
  type: TreasuryAccountType;
  accountNumber?: string;
  bankName?: string;
  currency: CurrencyCode;
  balance: number;
  isActive: boolean;
  businessUnitId?: string;
  businessUnitType?: string;
  createdAt: string;
  updatedAt: string;
}

// Réexporter le type local pour compatibilité
export type TreasuryAccount = LocalTreasuryAccount;

/**
 * Interface CashFlow conforme à la documentation API
 */
export interface CashFlow {
  id: string;
  accountId: string;                    // Aligné avec la doc
  type: CashFlowType;                   // 'inflow' | 'outflow' au lieu de 'income' | 'expense'
  category: CashFlowCategory;           // Catégories typées
  date: string;
  amount: number;
  currency: CurrencyCode;
  description: string;
  reference?: string;
  reconciled: boolean;                  // Ajouté conformément à la doc
  status?: 'pending' | 'completed' | 'cancelled';
  businessUnitId?: string;
  createdAt: string;
}

/**
 * Résumé de trésorerie conforme à la documentation
 */
export interface TreasurySummary {
  totalBalance: number;
  totalInflows: number;                 // Renommé de totalIncome
  totalOutflows: number;                // Renommé de totalExpense
  netCashFlow: number;
  currency: CurrencyCode;
  period: {
    startDate: string;
    endDate: string;
  };
  accountsBreakdown: Array<{
    accountId: string;
    accountName: string;
    accountType: TreasuryAccountType;
    balance: number;
    currency: CurrencyCode;
  }>;
  trends?: {
    inflowTrend: number;
    outflowTrend: number;
  };
}

/**
 * Prévision de trésorerie conforme à la documentation
 */
export interface TreasuryForecast {
  forecastPeriod: '7d' | '30d' | '90d';
  startDate: string;
  endDate: string;
  currentBalance: number;
  projectedBalance: number;
  expectedInflows: number;
  expectedOutflows: number;
  confidence: 'high' | 'medium' | 'low';
  dailyProjections: Array<{
    date: string;
    projectedBalance: number;
    expectedInflow: number;
    expectedOutflow: number;
  }>;
  alerts?: ForecastAlert[];
}

export interface ForecastAlert {
  type: 'warning' | 'danger';
  date: string;
  message: string;
  projectedBalance: number;
}

/**
 * Paramètres de recherche pour la trésorerie - conformes à la documentation
 */
export interface TreasurySearchParams extends BusinessUnitFilterBase {
  startDate?: string;
  endDate?: string;
  accountId?: string;
  type?: CashFlowType;
  category?: CashFlowCategory;
  reconciled?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Paramètres pour les requêtes de trésorerie
 */
export interface TreasuryParams extends BusinessUnitFilterBase {
  fiscalYearId?: string;
  startDate?: string;
  endDate?: string;
  currency?: CurrencyCode;
}

/**
 * Élément de réconciliation
 */
export interface ReconciliationItem {
  id: string;
  cashFlowId: string;
  bankStatementRef?: string;
  amount: number;
  date: string;
  status: 'matched' | 'unmatched' | 'pending';
  matchedAt?: string;
}

/**
 * API Service pour la gestion de la trésorerie
 * Conforme à la documentation treasury.md
 */
export const treasuryApi = {
  // ============================================
  // COMPTES DE TRÉSORERIE
  // ============================================

  /**
   * Récupérer tous les comptes de trésorerie
   * @see GET /treasury/accounts
   */
  getAccounts: (params?: BusinessUnitFilterBase & { 
    type?: TreasuryAccountType;
    isActive?: boolean;
  }): Promise<ApiResponse<{ success: boolean; data: TreasuryAccountApi[] }>> =>
    ApiService.get<{ success: boolean; data: TreasuryAccountApi[] }>('/treasury/accounts', params),

  /**
   * Récupérer un compte de trésorerie par ID
   * @see GET /treasury/accounts/:id
   */
  getAccountById: (accountId: string): Promise<ApiResponse<{ success: boolean; data: TreasuryAccountApi }>> =>
    ApiService.get<{ success: boolean; data: TreasuryAccountApi }>(`/treasury/accounts/${accountId}`),

  /**
   * Créer un nouveau compte de trésorerie
   * @see POST /treasury/accounts
   */
  createAccount: (data: Omit<TreasuryAccountApi, 'id' | 'createdAt' | 'updatedAt' | 'balance'>): Promise<ApiResponse<{ success: boolean; data: TreasuryAccountApi }>> =>
    ApiService.post<{ success: boolean; data: TreasuryAccountApi }>('/treasury/accounts', data),

  /**
   * Mettre à jour un compte de trésorerie
   * @see PUT /treasury/accounts/:id
   */
  updateAccount: (accountId: string, data: Partial<TreasuryAccountApi>): Promise<ApiResponse<{ success: boolean; data: TreasuryAccountApi }>> =>
    ApiService.put<{ success: boolean; data: TreasuryAccountApi }>(`/treasury/accounts/${accountId}`, data),

  /**
   * Supprimer un compte de trésorerie
   * @see DELETE /treasury/accounts/:id
   */
  deleteAccount: (accountId: string): Promise<ApiResponse<void>> =>
    ApiService.delete<void>(`/treasury/accounts/${accountId}`),

  // ============================================
  // MOUVEMENTS DE TRÉSORERIE (CASH FLOWS)
  // ============================================

  /**
   * Récupérer tous les mouvements de trésorerie
   * @see GET /treasury/cash-flows
   */
  getCashFlows: (params?: TreasurySearchParams): Promise<ApiResponse<{ success: boolean; data: CashFlow[]; pagination: any }>> =>
    ApiService.get<{ success: boolean; data: CashFlow[]; pagination: any }>('/treasury/cash-flows', params),

  /**
   * Récupérer un mouvement par ID
   * @see GET /treasury/cash-flows/:id
   */
  getCashFlowById: (cashFlowId: string): Promise<ApiResponse<{ success: boolean; data: CashFlow }>> =>
    ApiService.get<{ success: boolean; data: CashFlow }>(`/treasury/cash-flows/${cashFlowId}`),

  /**
   * Créer un nouveau mouvement de trésorerie
   * @see POST /treasury/cash-flows
   */
  createCashFlow: (data: Omit<CashFlow, 'id' | 'createdAt' | 'reconciled'>): Promise<ApiResponse<{ success: boolean; data: CashFlow }>> =>
    ApiService.post<{ success: boolean; data: CashFlow }>('/treasury/cash-flows', data),

  /**
   * Mettre à jour un mouvement de trésorerie
   * @see PUT /treasury/cash-flows/:id
   * Note: Les flux rapprochés ne peuvent pas être modifiés
   */
  updateCashFlow: (cashFlowId: string, data: Partial<CashFlow>): Promise<ApiResponse<{ success: boolean; data: CashFlow }>> =>
    ApiService.put<{ success: boolean; data: CashFlow }>(`/treasury/cash-flows/${cashFlowId}`, data),

  /**
   * Supprimer un mouvement de trésorerie
   * @see DELETE /treasury/cash-flows/:id
   * Note: Les flux rapprochés ne peuvent pas être supprimés
   */
  deleteCashFlow: (cashFlowId: string): Promise<ApiResponse<void>> =>
    ApiService.delete<void>(`/treasury/cash-flows/${cashFlowId}`),

  // ============================================
  // RÉSUMÉ ET RAPPORTS
  // ============================================

  /**
   * Récupérer le résumé de la trésorerie
   * @see GET /treasury/summary
   */
  getSummary: (params?: TreasuryParams): Promise<ApiResponse<{ success: boolean; data: TreasurySummary }>> =>
    ApiService.get<{ success: boolean; data: TreasurySummary }>('/treasury/summary', params),

  /**
   * Récupérer les prévisions de trésorerie
   * @see GET /treasury/forecast
   * @param params.period - Période de prévision: '7d', '30d', '90d'
   */
  getForecast: (params?: TreasuryParams & { 
    period?: '7d' | '30d' | '90d';
  }): Promise<ApiResponse<{ success: boolean; data: TreasuryForecast }>> =>
    ApiService.get<{ success: boolean; data: TreasuryForecast }>('/treasury/forecast', params),

  /**
   * Exporter les données de trésorerie
   * @see GET /treasury/export
   */
  exportData: (params: TreasuryParams & {
    format: 'pdf' | 'excel' | 'csv';
    type: 'summary' | 'cash-flows' | 'accounts';
  }): Promise<ApiResponse<Blob>> =>
    ApiService.get<Blob>('/treasury/export', params),

  // ============================================
  // RÉCONCILIATION BANCAIRE
  // Note: Les routes utilisent /treasury/reconciliation (nouvelles routes)
  // Les anciennes routes /treasury/accounts/:id/reconciliation sont dépréciées
  // ============================================

  /**
   * Lister les réconciliations par compte
   * @see GET /treasury/reconciliation?accountId=xxx
   */
  getReconciliations: (params: {
    accountId: string;
    startDate?: string;
    endDate?: string;
    status?: 'matched' | 'unmatched' | 'pending';
  }): Promise<ApiResponse<{ success: boolean; data: ReconciliationItem[] }>> =>
    ApiService.get<{ success: boolean; data: ReconciliationItem[] }>('/treasury/reconciliation', params),

  /**
   * Effectuer une réconciliation
   * @see POST /treasury/reconciliation
   */
  createReconciliation: (data: {
    accountId: string;
    bankStatementDate: string;
    bankStatementBalance: number;
    matchedTransactions: string[];
  }): Promise<ApiResponse<{ success: boolean; data: { matched: number; difference: number } }>> =>
    ApiService.post<{ success: boolean; data: { matched: number; difference: number } }>('/treasury/reconciliation', data),

  // ============================================
  // MÉTHODES DÉPRÉCIÉES (maintenues pour rétrocompatibilité)
  // ============================================

  /**
   * @deprecated Utiliser getReconciliations à la place
   */
  getReconciliationItems: (accountId: string, params?: {
    startDate?: string;
    endDate?: string;
    status?: 'matched' | 'unmatched' | 'all';
  }): Promise<ApiResponse<any[]>> =>
    ApiService.get<any[]>(`/treasury/accounts/${accountId}/reconciliation`, params),

  /**
   * @deprecated Utiliser createReconciliation à la place
   */
  reconcile: (accountId: string, data: {
    bankStatementDate: string;
    bankStatementBalance: number;
    matchedTransactions: string[];
  }): Promise<ApiResponse<{ matched: number; difference: number }>> =>
    ApiService.post<{ matched: number; difference: number }>(`/treasury/accounts/${accountId}/reconciliation`, data)
};
